// Words for what a booklet's print job reports (stages, errors, problems, warnings) and for its documents.
import { formatDateTime } from "../../../utils/formatDate";

// The job's stages the server names (anything else is shown generically).
const STAGES = [
  "queued",
  "paused",
  "waiting_exams",
  "waiting_memory",
  "waiting_renderer",
  "preparing",
  "launched",
  "loading",
  "printing_student",
  "printing_key",
  "merging",
  "printed",
  "saving",
  "retrying",
  "done",
];
// Why a job failed (error.code).
const JOB_ERRORS = [
  "booklet_content",
  "renderer_busy",
  "server_busy",
  "render_timeout",
  "render_too_large",
  "renderer_crashed",
  "renderer_unavailable",
  "layout_mismatch",
  "worker_lost",
  "enqueue_failed",
  "internal_error",
  "render_failed",
  "furniture_failed",
  // The private storage would hand the files to anyone, or could not be shown not to: nothing was saved.
  "storage_not_private",
  "storage_unchecked",
];
// What in the content cannot be printed (booklet_content's problems; a preview prints them as warnings).
const PROBLEMS = [
  "external_image",
  "image_unavailable",
  "image_unreadable",
  "unsupported_image",
  "image_too_large",
  "images_too_large",
  "local_file_image",
  "image_no_source",
  "image_failed",
  "katex_error",
  "page_error",
  "blocked_request",
  "pua_glyph",
];

export const stageLabel = (t, stage) =>
  STAGES.includes(stage) ? t(`bts.print.stage.${stage}`) : t("bts.print.stage.unknown");

export const jobErrorLabel = (t, code) =>
  JOB_ERRORS.includes(code) ? t(`bts.print.job_error.${code}`) : t("bts.print.job_error.unknown", { code: code || "" });

export const problemLabel = (t, code) =>
  PROBLEMS.includes(code) ? t(`bts.print.problem.${code}`) : t("bts.print.problem.unknown", { code: code || "" });

// A warning of a finished job: a question split between columns, content scaled down (factor 0.82 → 82%), a glyph
// the fonts lack, or (in a preview) a problem that would stop the final PDFs.
export const warningLabel = (t, item) => {
  if (item.code === "question_split") return t("bts.print.warning.question_split");
  if (item.code === "content_scaled") {
    const factor = Number(item.factor);
    return Number.isFinite(factor) && factor > 0
      ? t("bts.print.warning.content_scaled", { percent: Math.round(factor * 100) })
      : t("bts.print.warning.content_scaled_plain");
  }
  if (PROBLEMS.includes(item.code)) return t(`bts.print.problem.${item.code}`);
  return t("bts.print.warning.unknown", { code: item.code || "" });
};

// A section (from 0) as the booklet names it: its variant's heading, else the variant's name.
export const sectionName = (booklet, section) => {
  const variant = booklet?.check?.sections?.[section]?.variant;
  return variant?.heading || variant?.name || null;
};

// Where in a question: its stem, an option ("option B"), the passage or the subject's introduction.
const partLabel = (t, part) => {
  if (!part) return null;
  if (part === "stem" || part === "passage" || part === "intro") return t(`bts.print.where.part_${part}`);
  const option = /^option\s+(.+)$/.exec(part);
  return option ? t("bts.print.where.part_option", { letter: option[1] }) : part;
};

// "МАТЕМАТИКА, 14-тапсырма, B жауабы": the place of a problem or warning ({variant, section, number, part}).
export const placeLabel = (t, booklet, item) => {
  const hasSection = item.section !== null && item.section !== undefined;
  const subject = hasSection
    ? sectionName(booklet, item.section) || t("bts.print.where.section", { n: Number(item.section) + 1 })
    : null;
  let place;
  if (subject && item.number !== null && item.number !== undefined) {
    place = t("bts.print.where.question", { subject, number: item.number });
  } else if (subject) {
    place = subject;
  } else if (item.variant) {
    place = t("bts.print.where.variant", { code: item.variant });
  } else {
    place = t("bts.print.where.booklet");
  }
  const part = partLabel(t, item.part);
  return part ? `${place}, ${part}` : place;
};

// The same problem or warning in several variants once: [{item, variants: [code]}], in their first order.
export const groupByVariant = (items) => {
  const groups = new Map();
  (items || []).forEach((item) => {
    const { variant, ...rest } = item || {};
    const key = JSON.stringify(rest, Object.keys(rest).sort());
    if (!groups.has(key)) groups.set(key, { item, variants: [] });
    if (variant && !groups.get(key).variants.includes(variant)) groups.get(key).variants.push(variant);
  });
  return [...groups.values()];
};

const LOCALES = { kk: "kk-KZ", ru: "ru-RU", en: "en-US" };

// "233 КБ", "1,2 МБ".
export const formatSize = (t, bytes, language) => {
  const value = Number(bytes) || 0;
  const format = new Intl.NumberFormat(LOCALES[language] || LOCALES.kk, { maximumFractionDigits: 1 });
  if (value < 1024 * 1024) return t("bts.print.size_kb", { value: format.format(Math.max(1, Math.round(value / 1024))) });
  return t("bts.print.size_mb", { value: format.format(Math.round((value / (1024 * 1024)) * 10) / 10) });
};

// "26.10.2026, 15:37:12": to the second, so that two versions of a document made in one minute differ.
export const formatExactTime = (value, language) => {
  const time = formatDateTime(value, language);
  return time ? `${time}:${String(new Date(value).getSeconds()).padStart(2, "0")}` : "";
};

// An account as the API gives it ({first_name, last_name, username} or {full_name, username}).
export const personName = (user) =>
  user?.full_name || [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() || user?.username || "";
